import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentPropsWithRef } from "react";
import { cn } from "@/utils/style.ts";

const cardVariants = cva("diagnostics-card", {
    variants: {
        variant: {
            info: "diagnostics-card--info",
            success: "diagnostics-card--success",
            warning: "diagnostics-card--warning",
            error: "diagnostics-card--error",
        },
    },
    defaultVariants: {
        variant: "info",
    },
});

export type CardVariants = VariantProps<typeof cardVariants>;
type DiagnosticsCardProps = ComponentPropsWithRef<"div"> & CardVariants;
export function DiagnosticsCard({ className, variant, ...props }: DiagnosticsCardProps) {
    return <div {...props} className={cardVariants({ variant, className })} />;
}

const gridVariants = cva("diagnostics-grid", {
    variants: {
        size: {
            sm: "diagnostics-grid--sm",
            md: "diagnostics-grid--md",
        },
    },
    defaultVariants: {
        size: "md",
    },
});

type GridVariants = VariantProps<typeof gridVariants>;
type DiagnosticsGridProps = ComponentPropsWithRef<"div"> & GridVariants;
export function DiagnosticsGrid({ className, size, ...props }: DiagnosticsGridProps) {
    return <div {...props} className={gridVariants({ size, className })} />;
}

type DiagnosticsEmptyProps = ComponentPropsWithRef<"p">;
export function DiagnosticsEmpty({ className, ...props }: DiagnosticsEmptyProps) {
    return <p {...props} className={cn("diagnostics-empty", className)} />;
}

type DiagnosticsCardHeaderProps = ComponentPropsWithRef<"div">;
export function DiagnosticsCardHeader({ className, ...props }: DiagnosticsCardHeaderProps) {
    return <div {...props} className={cn("diagnostics-card__header", className)} />;
}

type DiagnosticsCardIconProps = {
    icon: React.ComponentType<{ className?: string }>;
    className?: string;
};
export function DiagnosticsCardIcon({ icon: Icon, className }: DiagnosticsCardIconProps) {
    return <Icon className={cn("diagnostics-card__icon", className)} />;
}

type DiagnosticsCardLabelProps = ComponentPropsWithRef<"span">;
export function DiagnosticsCardLabel({ className, ...props }: DiagnosticsCardLabelProps) {
    return <span {...props} className={cn("diagnostics-card__label", className)} />;
}

type DiagnosticsCardValueProps = ComponentPropsWithRef<"span">;
export function DiagnosticsCardValue({ className, ...props }: DiagnosticsCardValueProps) {
    return <span {...props} className={cn("diagnostics-card__value", className)} />;
}

type DiagnosticsCardDescriptionProps = ComponentPropsWithRef<"p">;
export function DiagnosticsCardDescription({ className, ...props }: DiagnosticsCardDescriptionProps) {
    return <p {...props} className={cn("diagnostics-card__description", className)} />;
}

type DiagnosticsCardNullProps = ComponentPropsWithRef<"em">;
export function DiagnosticsCardNull({ className, ...props }: DiagnosticsCardNullProps) {
    return <em {...props} className={cn("diagnostics-card__null", className)} />;
}
